import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Activity, ArrowLeft, RefreshCcw, Loader2, User, Mail, FileText,
  Package, Bell, CreditCard, ClipboardList, Home as HomeIcon,
  ChevronDown, ChevronUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map every category to a lucide icon + accent color so the feed
// scans quickly without reading every description.
const CATEGORY_META = {
  clients:         { icon: User,          color: 'text-emerald-300', label: 'Clients' },
  client_updates:  { icon: Mail,          color: 'text-cyan-300',    label: 'Client Updates' },
  disputes:        { icon: FileText,      color: 'text-rose-300',    label: 'Disputes & Mail' },
  ftc:             { icon: FileText,      color: 'text-orange-300',  label: 'FTC Reports' },
  tradelines:      { icon: CreditCard,    color: 'text-amber-300',   label: 'Tradelines' },
  bills:           { icon: HomeIcon,      color: 'text-lime-300',    label: 'Bills' },
  emails:          { icon: Bell,          color: 'text-indigo-300',  label: 'Emails' },
  todos:           { icon: ClipboardList, color: 'text-fuchsia-300', label: 'To-Dos' },
  leases:          { icon: FileText,      color: 'text-sky-300',     label: 'Leases' },
  system_upgrades: { icon: Package,       color: 'text-violet-300',  label: 'App Upgrades' },
  misc:            { icon: Activity,      color: 'text-slate-300',   label: 'Other' },
};

const RANGES = [
  { hours: 24,  label: '24 Hours' },
  { hours: 168, label: 'Last Week' },
  { hours: 720, label: 'Last 30 Days' },
];

function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = Date.now();
  const ms = now - d.getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

function formatAbsolute(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ActivityLogPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState(null); // category key or null
  const [rangeHours, setRangeHours] = useState(168); // default to Last Week
  const [expanded, setExpanded] = useState({});  // rowId -> boolean

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/activity-log/last-24h`,
        { params: { hours: rangeHours }, timeout: 15000 }
      );
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      console.error('[ActivityLog] load failed', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [rangeHours]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = filter ? items.filter((it) => it.category === filter) : items;
  const total = items.length;
  const currentRange = RANGES.find(r => r.hours === rangeHours) || RANGES[1];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-10">
        {/* ── Header w/ Back + Refresh ── */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-emerald-300 hover:text-emerald-200 border border-emerald-400/30 hover:border-emerald-300/60 rounded-full px-3 py-1.5 transition"
            data-testid="activity-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-slate-300 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-3 py-1.5 transition disabled:opacity-40"
            data-testid="activity-refresh-btn"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>

        <div className="mb-6">
          <h1 className="flex items-center gap-3 text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-br from-emerald-300 via-teal-300 to-cyan-400 bg-clip-text text-transparent">
            <Activity className="w-8 h-8 text-emerald-300" />
            {currentRange.label}
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Every user-visible action across the platform. Tap any row to see full details.
          </p>
        </div>

        {/* ── Range switcher ── */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="activity-range-switcher">
          {RANGES.map(r => (
            <button
              key={r.hours}
              type="button"
              onClick={() => { setRangeHours(r.hours); setExpanded({}); }}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold border transition ${
                rangeHours === r.hours
                  ? 'bg-emerald-500/20 border-emerald-400/60 text-emerald-200'
                  : 'bg-white/[0.04] border-white/10 text-slate-400 hover:bg-white/10'
              }`}
              data-testid={`activity-range-${r.hours}h`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* ── Counts pills ── */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="activity-counts">
          <button
            type="button"
            onClick={() => setFilter(null)}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold border transition ${
              filter === null
                ? 'bg-white/15 border-white/40 text-white'
                : 'bg-white/[0.04] border-white/10 text-slate-400 hover:bg-white/10'
            }`}
            data-testid="activity-filter-all"
          >
            All · {total}
          </button>
          {Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .map(([cat, n]) => {
              const meta = CATEGORY_META[cat] || CATEGORY_META.misc;
              const Icon = meta.icon;
              const active = filter === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setFilter(active ? null : cat)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                    active
                      ? 'bg-white/15 border-white/40 text-white'
                      : 'bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/10'
                  }`}
                  data-testid={`activity-filter-${cat}`}
                >
                  <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                  {meta.label} · {n}
                </button>
              );
            })}
        </div>

        {/* ── Feed ── */}
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 py-12 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading activity…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 rounded-xl border border-white/10 bg-slate-900/40 text-center" data-testid="activity-empty">
            <Activity className="w-8 h-8 text-slate-500 mx-auto mb-3" />
            <div className="text-slate-300 font-semibold">
              Nothing yet in {currentRange.label.toLowerCase()}{filter ? ` for ${(CATEGORY_META[filter] || CATEGORY_META.misc).label}` : ''}.
            </div>
            <div className="text-slate-500 text-sm mt-1">
              Try a wider range, or as you use the platform this feed will fill in automatically.
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="activity-feed">
            {filtered.map((it) => {
              const meta = CATEGORY_META[it.category] || CATEGORY_META.misc;
              const Icon = meta.icon;
              const rowId = it.id || `${it.event_type}-${it.created_at}`;
              const isOpen = !!expanded[rowId];
              const longDesc = (it.meta && (it.meta.description || it.meta.body)) || '';
              const hasDetails = !!(longDesc || it.subject_label || it.actor);
              return (
                <button
                  key={rowId}
                  type="button"
                  onClick={() => setExpanded(prev => ({ ...prev, [rowId]: !prev[rowId] }))}
                  className={`w-full text-left flex flex-col gap-2 p-3 rounded-lg bg-slate-900/50 border transition hover:border-emerald-400/40 ${
                    isOpen ? 'border-emerald-400/40' : 'border-white/5'
                  }`}
                  data-testid={`activity-row-${it.event_type}`}
                  aria-expanded={isOpen}
                >
                  <div className="flex items-start gap-3 w-full">
                    <div className="w-9 h-9 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center flex-shrink-0">
                      <Icon className={`w-4 h-4 ${meta.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm text-white font-medium ${isOpen ? '' : 'truncate'}`}>
                        {it.description}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className={`uppercase tracking-wider font-bold ${meta.color}`}>{meta.label}</span>
                        <span>·</span>
                        <span>{formatRelative(it.created_at)}</span>
                        {it.subject_label && (
                          <>
                            <span>·</span>
                            <span className={`text-slate-300 ${isOpen ? '' : 'truncate'} max-w-xs`}>{it.subject_label}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {hasDetails && (
                      <div className="flex-shrink-0 pt-1 text-slate-500">
                        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    )}
                  </div>
                  {isOpen && hasDetails && (
                    <div
                      className="ml-12 pl-1 border-l border-emerald-400/20 pr-2 py-2 space-y-1"
                      data-testid={`activity-row-details-${rowId}`}
                    >
                      {longDesc && (
                        <div className="text-[13px] text-slate-200 leading-relaxed whitespace-pre-wrap">
                          {longDesc}
                        </div>
                      )}
                      <div className="text-[10px] text-slate-500 flex flex-wrap items-center gap-2 mt-1">
                        <span>{formatAbsolute(it.created_at)}</span>
                        {it.actor && (
                          <>
                            <span>·</span>
                            <span>by {it.actor}</span>
                          </>
                        )}
                        {it.event_type && (
                          <>
                            <span>·</span>
                            <span className="font-mono">{it.event_type}</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
